<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="640px">
    <v-card class="profile-dialog-card assignment-dialog">
      <div class="profile-dialog-header assignment-dialog__header">
        <div class="assignment-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">{{ entityIcon }}</v-icon>
          </v-avatar>
          <div>
            <span>{{ sourceLabel }}</span>
            <h2>Assign {{ typeLabel.toLowerCase() }}</h2>
            <p>Add or remove {{ typeLabel.toLowerCase() }} for this classroom.</p>
          </div>
        </div>
        <v-btn icon :aria-label="`Close ${typeLabel.toLowerCase()} dialog`" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text v-if="classroom" class="profile-dialog-body assignment-dialog__body">
        <section class="assignment-dialog__classroom">
          <div class="assignment-dialog__classroom-icon">
            <v-icon color="primary">mdi-door-open</v-icon>
          </div>
          <div>
            <strong>{{ classroom.name }}</strong>
            <span>{{ departmentName }}<template v-if="curriculumName"> · {{ curriculumName }}</template></span>
          </div>
          <v-chip small outlined color="primary">{{ assignments.length }} assigned</v-chip>
        </section>

        <div class="assignment-dialog__picker">
          <v-autocomplete
            v-model="selected"
            :items="availablePeople"
            :item-text="displayName"
            item-value="id"
            return-object
            clearable
            outlined
            dense
            hide-details
            :loading="loading"
            :label="`Select ${singleLabel}`"
            :menu-props="{ offsetY: true, maxHeight: 300 }"
          />
          <v-btn color="primary" depressed :disabled="!selected" :loading="saving" @click="add">
            <v-icon left small>mdi-plus</v-icon>
            Add
          </v-btn>
        </div>

        <div class="assignment-dialog__section-heading">
          <div><strong>Assigned {{ typeLabel.toLowerCase() }}</strong><span>People currently connected to {{ classroom.name }}.</span></div>
        </div>

        <div v-if="loading" class="assignment-dialog__loading">
          <v-progress-circular indeterminate color="primary" size="32" width="3" />
          <span>Loading {{ typeLabel.toLowerCase() }}…</span>
        </div>

        <div v-else-if="assignments.length" class="assignment-dialog__list">
          <div v-for="row in assignments" :key="row.id" class="assignment-dialog__row">
            <v-avatar size="38" color="green lighten-5">
              <span class="primary--text font-weight-bold">{{ initials(assignmentName(row)) }}</span>
            </v-avatar>
            <div><strong>{{ assignmentName(row) }}</strong><span>{{ singleLabel }}</span></div>
            <v-btn icon color="error" :aria-label="`Remove ${assignmentName(row)}`" @click="remove(row)">
              <v-icon size="19">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-else class="assignment-dialog__empty">
          <v-icon size="38" color="grey lighten-1">{{ entityIcon }}</v-icon>
          <strong>No {{ typeLabel.toLowerCase() }} assigned</strong>
          <span>Use the selector above to add the first {{ singleLabel }}.</span>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="profile-dialog-actions assignment-dialog__actions">
        <span>{{ assignments.length }} assigned</span>
        <v-spacer />
        <v-btn text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import teacherService from "@/modules/Teacher/service.js";
import studentService from "@/modules/Student/service.js";
import classRoomService from "@/modules/ClassRoom/service.js";
import virtualClassRoomService from "@/modules/VirtualClassRoom/service.js";

export default {
  name: "classroom-assignments",
  props: {
    value: Boolean,
    classroom: { type: Object, default: null },
    type: { type: String, required: true },
    source: { type: String, default: "regular" },
  },
  data: () => ({ dialog: false, loading: false, saving: false, selected: null, people: [], assignments: [] }),
  computed: {
    typeLabel() { return this.type === "student" ? "Students" : "Teachers"; },
    singleLabel() { return this.type === "student" ? "student" : "teacher"; },
    sourceLabel() { return this.source === "virtual" ? "Virtual classroom" : "Classroom"; },
    entityIcon() { return this.type === "student" ? "mdi-account-school-outline" : "mdi-account-tie-outline"; },
    departmentName() { return this.classroom && this.classroom.Class ? this.classroom.Class.name : "No department"; },
    curriculumName() {
      if (!this.classroom) return "";
      if (this.classroom.Curriculum) return this.classroom.Curriculum.name;
      if (Array.isArray(this.classroom.Curriculums)) return this.classroom.Curriculums.map((row) => row.name).join(", ");
      return "";
    },
    availablePeople() {
      const assigned = this.assignments.map((row) => this.personId(row));
      return this.people.filter((person) => !assigned.includes(person.id));
    },
  },
  watch: {
    value: { immediate: true, handler(value) { this.dialog = value; if (value) this.load(); } },
    dialog(value) { if (!value && this.value) this.$emit("input", false); },
  },
  methods: {
    displayName(person) { return person.fullName || person.name || [person.firstName, person.lastName].filter(Boolean).join(" "); },
    person(row) { return this.type === "student" ? row.Student : row.Teacher; },
    personId(row) { const person = this.person(row); return person ? person.id : row[this.type === "student" ? "StudentId" : "TeacherId"]; },
    assignmentName(row) { return this.displayName(this.person(row) || {}) || "Unknown"; },
    initials(name) { return String(name || "?").split(" ").filter(Boolean).slice(0, 2).map((part) => part.charAt(0)).join("").toUpperCase(); },
    assignmentService() {
      return this.source === "virtual" && this.type === "student"
        ? virtualClassRoomService
        : classRoomService;
    },
    async load() {
      if (!this.classroom) return;
      this.loading = true;
      this.selected = null;
      try {
        const peopleRequest = this.type === "student"
          ? studentService.getAll({ ClassId: this.classroom.ClassId })
          : teacherService.getAll();
        const params = this.source === "virtual"
          ? { VirtualClassRoomId: this.classroom.id }
          : { ClassRoomId: this.classroom.id };
        const service = this.assignmentService();
        const assignmentRequest = this.type === "student"
          ? (this.source === "virtual" ? service.getStudents(params) : service.getAssignStudentList(params))
          : service.getAssignTeacherList(params);
        const responses = await Promise.all([peopleRequest, assignmentRequest]);
        this.people = responses[0].data || [];
        this.assignments = responses[1].data || [];
      } catch (error) {
        this.showError(error, `Unable to load ${this.typeLabel.toLowerCase()}.`);
      } finally {
        this.loading = false;
      }
    },
    async add() {
      if (!this.selected) return;
      this.saving = true;
      try {
        const idKey = this.source === "virtual" ? "VirtualClassRoomId" : "ClassRoomId";
        const payload = { [idKey]: this.classroom.id, [this.type === "student" ? "StudentId" : "TeacherId"]: this.selected.id };
        const service = this.assignmentService();
        const response = this.type === "student" ? await service.assignStudent(payload) : await service.assignTeacher(payload);
        this.assignments.push(response.data);
        this.selected = null;
        this.$emit("changed");
      } catch (error) {
        this.showError(error, `Unable to assign ${this.singleLabel}.`);
      } finally {
        this.saving = false;
      }
    },
    remove(row) {
      this.$swal({ title: `Remove ${this.singleLabel}?`, text: `${this.assignmentName(row)} will no longer belong to this classroom.`, icon: "question", showCancelButton: true, confirmButtonColor: "#B71C1C", confirmButtonText: "Remove" }).then(async (result) => {
        if (!result.value) return;
        try {
          const service = this.assignmentService();
          if (this.type === "student") await service.removeStudent(row.id); else await service.removeTeacher(row.id);
          this.assignments = this.assignments.filter((item) => item.id !== row.id);
          this.$emit("changed");
        } catch (error) { this.showError(error, `Unable to remove ${this.singleLabel}.`); }
      });
    },
    showError(error, fallback) { this.$swal({ title: "Assignment could not be changed", text: error && error.data && error.data.message ? error.data.message : fallback, icon: "error" }); },
    close() { this.dialog = false; this.$emit("input", false); },
  },
};
</script>

<style scoped>
.assignment-dialog { overflow: hidden; border-radius: 14px !important; }
.assignment-dialog__header { background: #fbfcfb; }
.assignment-dialog__title { display: flex; align-items: center; gap: 12px; }
.assignment-dialog__title > div:last-child { display: flex; flex-direction: column; }
.assignment-dialog__title span { color: #43a047; font-size: 10px; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
.assignment-dialog__title h2 { margin: 2px 0 0; color: #172033; font-size: 21px; line-height: 1.2; }
.assignment-dialog__title p { margin: 3px 0 0; color: #7b8799; font-size: 11px; }
.assignment-dialog__body { min-height: 390px; padding: 22px 24px !important; }
.assignment-dialog__classroom { display: flex; align-items: center; gap: 11px; padding: 13px; border: 1px solid #e3e9e3; border-radius: 11px; background: #f8faf8; }
.assignment-dialog__classroom-icon { display: flex; width: 38px; height: 38px; align-items: center; justify-content: center; border-radius: 9px; background: #eaf6eb; }
.assignment-dialog__classroom > div:nth-child(2) { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.assignment-dialog__classroom strong { color: #253047; font-size: 13px; }
.assignment-dialog__classroom span { margin-top: 2px; color: #879288; font-size: 10px; }
.assignment-dialog__picker { display: flex; align-items: center; gap: 10px; margin-top: 17px; }
.assignment-dialog__picker .v-btn { height: 40px; border-radius: 9px; text-transform: none; letter-spacing: 0; }
.assignment-dialog__section-heading { margin: 22px 0 8px; }
.assignment-dialog__section-heading > div { display: flex; flex-direction: column; }
.assignment-dialog__section-heading strong { color: #344238; font-size: 12px; }
.assignment-dialog__section-heading span { margin-top: 2px; color: #8b968e; font-size: 10px; }
.assignment-dialog__list { overflow: hidden; border: 1px solid #e5eae5; border-radius: 10px; }
.assignment-dialog__row { display: flex; align-items: center; gap: 10px; min-height: 58px; padding: 9px 11px; border-bottom: 1px solid #edf0ed; }
.assignment-dialog__row:last-child { border-bottom: 0; }
.assignment-dialog__row > div:nth-child(2) { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.assignment-dialog__row strong { color: #344238; font-size: 12px; }
.assignment-dialog__row span { margin-top: 2px; color: #8b968e; font-size: 9px; text-transform: capitalize; }
.assignment-dialog__loading, .assignment-dialog__empty { display: flex; min-height: 150px; flex-direction: column; align-items: center; justify-content: center; gap: 7px; color: #8b968e; text-align: center; }
.assignment-dialog__empty strong { color: #526057; font-size: 12px; }
.assignment-dialog__empty span, .assignment-dialog__loading span { font-size: 10px; }
.assignment-dialog__actions { color: #7b8799; font-size: 10px; }
.assignment-dialog__actions .v-btn { border-radius: 8px; text-transform: none; letter-spacing: 0; }
@media (max-width: 599px) { .assignment-dialog__body { min-height: 340px; padding: 16px !important; } .assignment-dialog__picker { align-items: stretch; flex-direction: column; } .assignment-dialog__picker .v-btn { width: 100%; } }
</style>
